import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Gauge, Calendar, Fuel, Settings2, ArrowUpRight, Pencil, Scale, Check, PlayCircle, LifeBuoy } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useCompare } from "@/context/CompareContext";

const fmtPrice = (v) =>
  typeof v === "number" && v > 0
    ? new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v)
    : "Prix sur demande";
const fmtKm = (v) => (typeof v === "number" ? new Intl.NumberFormat("fr-BE").format(v) + " km" : "-");

export default function VehicleCard({ vehicle }) {
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const { toggle, has, isFull } = useCompare();
  const inCompare = has(vehicle.id);
  const [loaded, setLoaded] = useState(false);
  const imgs = vehicle.images?.length ? vehicle.images : ["https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4807217354.jpg"];
  const baseImg = imgs[0];
  const hoverImg = imgs[1] || imgs[0];
  const isSold = vehicle.status === "sold";
  const hasVideo = Boolean(vehicle.video_url) || (Array.isArray(vehicle.videos) && vehicle.videos.some((v) => v?.url));

  return (
    <Link
      to={`/stock/${vehicle.id}`}
      data-testid={`vehicle-card-${vehicle.id}`}
      className={`group block bg-[#1E1E1E] border border-white/10 rounded-md overflow-hidden hover:border-[#C8A75A]/50 transition-colors duration-500 relative ${isSold ? "kv-sold" : ""}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-black crossfade">
        {/* Base image with fade-in on load */}
        <img
          src={baseImg}
          alt={`${vehicle.brand} ${vehicle.model}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={`cf-img cf-base ${loaded ? "img-fade-in-loaded" : "img-fade-pending"} ${isSold ? "grayscale-[70%] opacity-70" : ""}`}
        />
        {/* Hover image — crossfades in on hover */}
        {hoverImg !== baseImg && (
          <img
            src={hoverImg}
            alt=""
            loading="lazy"
            className={`cf-img cf-hover ${isSold ? "grayscale-[70%] opacity-70" : ""}`}
            aria-hidden
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10 pointer-events-none" />

        {/* Bouton overlay : ESSAYEZ-MOI SUR PISTE — top-right */}
        <button
          type="button"
          data-testid={`card-sim-cta-${vehicle.id}`}
          aria-label="Essayez-moi sur piste — Stage simulateur"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); nav("/simulator"); }}
          className="absolute top-3 right-3 z-30 inline-flex items-center gap-1.5 bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 px-3 py-1.5 rounded-md text-[10px] uppercase tracking-[0.2em] font-bold shadow-[0_6px_20px_-4px_rgba(200,167,90,0.7)] ring-1 ring-black/20"
        >
          <LifeBuoy className="w-3.5 h-3.5" strokeWidth={1.8} />
          Essayez-moi sur piste
        </button>

        {/* Bandeau statut RÉSERVÉ uniquement (bottom-left) — les véhicules vendus utilisent la diagonale centrale */}
        {vehicle.status === "reserved" && (
          <span
            data-testid={`status-badge-${vehicle.id}`}
            className="absolute bottom-3 left-3 z-30 inline-flex items-center bg-[#C8A75A] text-black px-3 py-1.5 rounded-md text-[10px] uppercase tracking-[0.2em] font-bold shadow-[0_6px_20px_-4px_rgba(200,167,90,0.7)] ring-1 ring-black/20"
          >
            {t.vehicle.status.reserved}
          </span>
        )}

        {/* Play icon overlay — bottom-right, ne gêne aucun autre badge */}
        {hasVideo && !isSold && (
          <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm border border-[#C8A75A]/50 rounded-full pl-2 pr-3 py-1.5 shadow-lg" data-testid={`card-video-badge-${vehicle.id}`}>
            <PlayCircle className="w-4 h-4 text-[#C8A75A]" strokeWidth={1.6} />
            <span className="text-[10px] uppercase tracking-widest text-white/90 font-medium">Vidéo</span>
          </div>
        )}

        {/* SOLD stamp diagonal — impact visuel supplémentaire pour les véhicules vendus */}
        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <span className="font-display text-4xl md:text-5xl tracking-[0.3em] uppercase text-white/95 border-y-2 border-[#C8A75A] px-6 py-2 -rotate-[8deg] bg-black/40 backdrop-blur-sm shadow-[0_8px_32px_rgba(0,0,0,0.6)]">
              {t.vehicle.status.sold}
            </span>
          </div>
        )}

        {user?.role === "admin" && (
          <a
            href={`/admin/vehicles?edit=${vehicle.id}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`quick-edit-${vehicle.id}`}
            title="Modifier ce véhicule (admin)"
            className="absolute top-14 right-3 z-30 w-10 h-10 rounded-full bg-[#C8A75A] text-black flex items-center justify-center shadow-lg hover:bg-white transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </a>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault(); e.stopPropagation();
            if (!inCompare && isFull()) { toast.error("Maximum 4 véhicules à comparer"); return; }
            toggle(vehicle.id);
            toast.success(inCompare ? "Retiré du comparateur" : "Ajouté au comparateur");
          }}
          data-testid={`compare-toggle-${vehicle.id}`}
          title={inCompare ? "Retirer du comparateur" : "Ajouter au comparateur"}
          className={`absolute z-30 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-colors right-3 ${
            user?.role === "admin" ? "top-[6.25rem]" : "top-14"
          } ${inCompare ? "bg-[#C8A75A] text-black" : "bg-black/70 backdrop-blur-md text-white hover:bg-[#C8A75A] hover:text-black"}`}
        >
          {inCompare ? <Check className="w-4 h-4" /> : <Scale className="w-4 h-4" />}
        </button>
      </div>

      <div className="p-8 lg:p-10">
        <div className="flex items-center justify-between gap-4 mb-2">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C8A75A]">{vehicle.brand}</p>
          <p className="text-xs text-white/50 tracking-widest">{vehicle.year}</p>
        </div>
        <h3 className="font-display text-3xl md:text-4xl text-white mb-6 leading-[1.1]">{vehicle.model}</h3>

        <div className="grid grid-cols-2 gap-4 text-sm text-white/60 mb-6">
          <span className="flex items-center gap-2"><Gauge className="w-4 h-4 text-[#C8A75A]/80" />{fmtKm(vehicle.mileage)}</span>
          <span className="flex items-center gap-2"><Fuel className="w-4 h-4 text-[#C8A75A]/80" />{vehicle.fuel_type}</span>
          <span className="flex items-center gap-2"><Settings2 className="w-4 h-4 text-[#C8A75A]/80" />{vehicle.transmission}</span>
          <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#C8A75A]/80" />{vehicle.power_hp} {t.vehicle.hp}</span>
        </div>

        <div className="pt-6 border-t border-white/10 flex items-baseline justify-between">
          <p className="font-display text-3xl md:text-4xl text-white tracking-tight">{fmtPrice(vehicle.price)}</p>
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 group-hover:text-[#C8A75A] transition-colors">
            Détails →
          </span>
        </div>
      </div>
    </Link>
  );
}
